import { CHARGE_TYPE_LABELS, RETURN_CONDITION_LABELS, type ActivityReport } from '@wms/domain';
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { api } from '../../api';
import { fmt } from '../../format';
import { GRAIN_TEXT, axisLabels, bucketPartial, bucketTitle, durationText, moneyListText, moneyText, partialBucketsText, percentText } from '../../performance';
import { Empty, ErrorBanner, Spinner, useLoad } from '../../ui';
import { Icon, type IconName } from '../../ui/icons';
import { ChannelsTable, ProductsTable, RegionsTable, SellersTable } from './ActivityTables';

/**
 * Siparişler, gönderim hızı, iadeler, ücretler, ürünler, kanallar ve bölgeler of the period: all of
 * it outlives the erasure of the buyers' personal data (KVKK), so a whole year can be looked back on.
 */
export function Activity(props: { query: string; operator: boolean; allSellers: boolean; onSeller: (tenantId: string) => void }) {
  const report = useLoad(() => api<ActivityReport>('GET', `/api/performance/activity?${props.query}`), [props.query], 300_000);
  if (report.error) return <ErrorBanner message={report.error} onRetry={() => void report.reload()} />;
  if (!report.data) return <Spinner />;
  const r = report.data;
  const quiet = r.orders.received === 0 && r.orders.shipped === 0 && r.orders.delivered === 0 && r.returns.orders === 0 && r.fees.totals.length === 0;
  if (quiet) {
    return (
      <section className="card">
        <Empty title="Bu dönemde sipariş, iade ya da ücret yok">
          <p>Daha uzun bir dönem seçin: “Son 3 ay”, “Son 12 ay” ya da “Bu yıl”.</p>
        </Empty>
      </section>
    );
  }
  return (
    <>
      <Orders r={r} />
      <SeriesChart r={r} />
      <Speed r={r} />
      <Returns r={r} />
      <Fees r={r} operator={props.operator} />
      {r.sellers && <SellersTable rows={r.sellers} onSeller={props.onSeller} />}
      <ProductsTable rows={r.products} count={r.productCount} showSeller={props.allSellers} />
      <ChannelsTable rows={r.channels} />
      <RegionsTable cities={r.cities} />
    </>
  );
}

function SectionTitle({ icon, children }: { icon: IconName; children: ReactNode }) {
  return (
    <h2 className="section-h">
      <span className="section-icon">
        <Icon name={icon} size={16} />
      </span>
      {children}
    </h2>
  );
}

function Tile(props: { value: string; label: string; hint: ReactNode; wide?: boolean }) {
  return (
    <div className={`card kpi${props.wide ? ' wide' : ''}${/^0([,.]0+)?$|^—$/.test(props.value) ? ' zero' : ''}`}>
      <span className={`n${props.value.length > 11 ? ' long' : ''}`}>{props.value}</span>
      <span className="l">{props.label}</span>
      <span className="h">{props.hint}</span>
    </div>
  );
}

function Orders({ r }: { r: ActivityReport }) {
  const o = r.orders;
  return (
    <section className="stack">
      <SectionTitle icon="list">Siparişler</SectionTitle>
      <div className="grid act-tiles three">
        <Tile value={fmt.n(o.received)} label="Alınan sipariş" hint="Sipariş tarihi bu dönemde olanlar, iptaller dahil" />
        <Tile value={fmt.n(o.units)} label="Adet" hint="Alınıp iptal edilmeyen siparişlerdeki ürün adedi" />
        <Tile value={fmt.n(o.cancelled)} label="İptal edilen" hint={`Alınan siparişlerden sonradan iptal edilen (${percentText(o.received ? o.cancelled / o.received : null)})`} />
        <Tile value={fmt.n(o.shipped)} label="Kargoya verilen" hint={`Bu dönemde kargo firmasına teslim edilen · ${fmt.n(o.shippedUnits)} adet`} />
        <Tile value={fmt.n(o.delivered)} label="Teslim edilen" hint="Bu dönemde alıcıya ulaşan (kargo takibine göre)" />
        <Tile
          value={moneyListText(o.sales)}
          label="Satış tutarı"
          hint={
            <>
              Alınıp iptal edilmeyen siparişlerin pazaryerindeki tutarı, pazaryerinin sonradan iptal ettiği ürünler düşülerek; tutarı gelmeyen
              siparişte satır fiyatları toplanır.
              {o.salesUnknown > 0 && ` ${fmt.n(o.salesUnknown)} siparişin tutarı bilinmiyor, katılmadı.`}
            </>
          }
        />
      </div>
    </section>
  );
}

/**
 * Received and shipped orders per day, week or month: two bars side by side, as wide as the card. A week or
 * month the period cuts is drawn pale and says its days; a phone too narrow for the bars opens on the latest.
 */
function SeriesChart({ r }: { r: ActivityReport }) {
  const box = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (box.current) box.current.scrollLeft = box.current.scrollWidth;
  }, [r]);
  const buckets = r.series;
  if (buckets.every((b) => !b.received && !b.shipped)) return null;
  const n = buckets.length;
  const max = Math.max(1, ...buckets.map((b) => Math.max(b.received, b.shipped)));
  // drawn on a 1000-wide canvas stretched to the card; the labels below are text, not drawn
  const width = 1000;
  const height = 150;
  const slot = width / n;
  const bar = Math.min(slot * 0.38, 40);
  const tall = (v: number) => Math.max(v > 0 ? 2 : 0, (v / max) * (height - 8));
  const labels = axisLabels(buckets, r.grain);
  const cut = partialBucketsText(buckets, r.grain);
  const series = [
    { key: 'received', label: 'Alınan', color: 'var(--brand)', value: (b: (typeof buckets)[number]) => b.received },
    { key: 'shipped', label: 'Kargoya verilen', color: 'var(--ok)', value: (b: (typeof buckets)[number]) => b.shipped },
  ];
  return (
    <section className="card">
      <div className="card-h">
        <Icon name="chart" />
        <h2>Alınan ve kargoya verilen</h2>
        <span className="sub">
          {GRAIN_TEXT[r.grain]} ·{' '}
          {series.map((s) => (
            <span key={s.key} className="legend">
              <i style={{ background: s.color }} /> {s.label}
            </span>
          ))}
        </span>
      </div>
      <div className="card-b series-box" ref={box}>
        <div className="series" style={{ minWidth: Math.min(n * 10, 900) }}>
          <span className="series-max">
            <span>{fmt.n(max)}</span>
          </span>
          <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" width="100%" height={height} role="img" aria-label="Alınan ve kargoya verilen siparişler">
            {[0.5, 1].map((y) => (
              <line key={y} x1={0} x2={width} y1={height - y * (height - 8)} y2={height - y * (height - 8)} stroke="var(--line)" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
            ))}
            <line x1={0} x2={width} y1={height} y2={height} stroke="var(--line-strong)" vectorEffect="non-scaling-stroke" />
            {buckets.map((b, i) =>
              series.map((s, j) => {
                const v = s.value(b);
                const h = tall(v);
                return (
                  <rect
                    key={`${b.from}-${s.key}`}
                    x={i * slot + slot / 2 + (j === 0 ? -bar - 1 : 1)}
                    y={height - h}
                    width={bar}
                    height={h}
                    fill={s.color}
                    fillOpacity={bucketPartial(b, r.grain) ? 0.4 : 1}
                  >
                    <title>{`${bucketTitle(b, r.grain)} · ${s.label}: ${fmt.n(v)}`}</title>
                  </rect>
                );
              }),
            )}
          </svg>
          <div className="series-axis" style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
            {buckets.map((b, i) => {
              const label = labels[i];
              return (
                <span key={b.from}>
                  {label?.main}
                  {label?.sub && <small>{label.sub}</small>}
                </span>
              );
            })}
          </div>
        </div>
      </div>
      {cut && <p className="card-b muted act-cut">{cut}</p>}
    </section>
  );
}

function Speed({ r }: { r: ActivityReport }) {
  const s = r.speed;
  return (
    <section className="stack">
      <SectionTitle icon="truck">Gönderim hızı</SectionTitle>
      <p className="muted act-note">Bu dönemde kargoya verilen {fmt.n(s.shipped)} sipariş; süreler sipariş anından kargo firmasına teslime kadar.</p>
      <div className="grid act-tiles">
        <Tile value={durationText(s.medianHours)} label="Kargoya verme süresi" hint="Ortanca: siparişlerin yarısı bundan kısa sürede kargoya verildi" />
        <Tile value={durationText(s.p90Hours)} label="Siparişlerin %90'ı" hint="Bu süre içinde kargoya verildi; kalan %10 daha uzun sürdü" />
        <Tile value={percentText(s.within24hRate)} label="24 saat içinde" hint={`${fmt.n(s.within24h)} sipariş, verildikten sonraki 24 saat içinde kargoya teslim edildi`} />
        <Tile
          value={fmt.n(s.late)}
          label="Geç kargoya verilen"
          hint={s.late > 0 ? `Son kargo zamanından sonra verildi; ortalama ${durationText(s.lateAvgHours)} gecikme` : 'Son kargo zamanını geçen sipariş yok'}
        />
        <Tile wide value={durationText(s.deliveryMedianHours)} label="Teslim süresi" hint={`Ortanca: bu dönemde teslim edilen ${fmt.n(s.delivered)} siparişte kargodan alıcıya geçen süre`} />
      </div>
    </section>
  );
}

function Returns({ r }: { r: ActivityReport }) {
  const x = r.returns;
  const sellable = x.byCondition.find((c) => c.condition === 'sellable')?.units ?? 0;
  return (
    <section className="stack">
      <SectionTitle icon="undo">İadeler</SectionTitle>
      <div className="grid act-tiles">
        <Tile value={fmt.n(x.orders)} label="İadesi gelen sipariş" hint={`Bu dönemde iade kabulde teslim alınan · ${fmt.n(x.units)} adet`} />
        <Tile
          value={percentText(x.rate)}
          label="İade oranı"
          hint={`Bu dönemde kargoya verilen ${fmt.n(x.shipped)} siparişten bugüne kadar iadesi gelen ${fmt.n(x.shippedReturned)}. Yeni gönderilerin iadesi henüz gelmemiş olabilir.`}
        />
        <Tile
          wide
          value={percentText(x.units ? sellable / x.units : null)}
          label="Yeniden satılabilir"
          hint={
            <>
              {x.byCondition.length > 0 && <b>{x.byCondition.map((c) => `${RETURN_CONDITION_LABELS[c.condition]} ${fmt.n(c.units)}`).join(' · ')} adet. </b>}
              Gelen ürünler, iade kabulde verilen karara göre; alıcının yazdığı neden burada gösterilmez.
            </>
          }
        />
      </div>
    </section>
  );
}

function Fees({ r, operator }: { r: ActivityReport; operator: boolean }) {
  const f = r.fees;
  return (
    <section className="card">
      <div className="card-h">
        <Icon name="receipt" />
        <h2>{operator ? 'Hizmet gelirleri' : 'Depo hizmet ücretleri'}</h2>
        <span className="sub">
          Hizmet tarihi bu dönemde olan ücretler, KDV hariç.
          {operator && ' Sipariş ve mal kabul ücretleri kendi deposunda sayılır; depolama ve diğer ücretler satıcı hesabına yazılır, depoya ayrılmaz.'}
        </span>
      </div>
      {f.byType.length === 0 ? (
        <Empty title="Bu dönemde ücret yok" />
      ) : (
        <div className="table-wrap">
          <table className="t totals">
            <thead>
              <tr>
                <th>Hizmet</th>
                <th className="num">Kalem</th>
                <th className="num">Tutar</th>
              </tr>
            </thead>
            <tbody>
              {f.byType.map((row) => (
                <tr key={`${row.type}-${row.currency}`}>
                  <td>{CHARGE_TYPE_LABELS[row.type]}</td>
                  <td className="num">{fmt.n(row.count)}</td>
                  <td className="num">{moneyText(row)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>Toplam</td>
                <td className="num">{fmt.n(f.byType.reduce((n, row) => n + row.count, 0))}</td>
                <td className="num">{moneyListText(f.totals)}</td>
              </tr>
              <tr>
                <td colSpan={2} title="Bütün ücretlerin toplamı, bu dönemde kargoya verilen sipariş sayısına bölünür">
                  Kargoya verilen sipariş başına ({fmt.n(r.orders.shipped)} sipariş)
                </td>
                <td className="num">{moneyListText(f.perShippedOrder)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}
